/**
 * QR コード表示。
 *
 * その場に全員がいるときは、URL を送るより QR を読ませるほうが速い。
 *
 * 依存は qrcode-genera­tor 1 つだけ (依存パッケージ 0 個) で、
 * 描画は自前で SVG の <rect> を並べる。画像生成やライブラリ側の
 * DOM 操作に頼らないため、React の外で何かが起きる余地がない。
 */
import qrcode from "qrcode-generator";
import { useMemo } from "react";

export interface QrCodeProps {
  value: string;
  /** 一辺のピクセル数 */
  size?: number;
  /** 余白 (モジュール数)。QR の仕様では 4 以上が推奨 */
  margin?: number;
  label?: string;
}

export function QrCode({ value, size = 176, margin = 2, label }: QrCodeProps) {
  const modules = useMemo(() => {
    // 誤り訂正レベル M: 汚れ・反射に対する耐性と情報量のバランスが良い
    const qr = qrcode(0, "M");
    qr.addData(value);
    qr.make();

    const count = qr.getModuleCount();
    const cells: Array<{ x: number; y: number }> = [];
    for (let row = 0; row < count; row += 1) {
      for (let col = 0; col < count; col += 1) {
        if (qr.isDark(row, col)) cells.push({ x: col, y: row });
      }
    }
    return { count, cells };
  }, [value]);

  const total = modules.count + margin * 2;

  return (
    <svg
      className="qr"
      width={size}
      height={size}
      viewBox={`0 0 ${total} ${total}`}
      role="img"
      aria-label={label ?? "共有 URL の QR コード"}
      shapeRendering="crispEdges"
    >
      {/* 読み取りには明るい余白が必要なので、背景は常に白で塗る */}
      <rect width={total} height={total} fill="#ffffff" />
      {modules.cells.map((cell) => (
        <rect
          key={`${cell.x}-${cell.y}`}
          x={cell.x + margin}
          y={cell.y + margin}
          width={1}
          height={1}
          fill="#101010"
        />
      ))}
    </svg>
  );
}
